import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import menuData from "../../data/dashboard.menu.json";
import { fetchMenuItems, bulkUpdateMenu } from "../../services/menu.service";
import { getUser } from "../../lib/authToken";
import { DEV_USER } from "../../config/localAuth";

export default function SideBarMenu() {
  // const ORG_ID = "101";
  // const LOCATION = "banjara-hill";

  const user = getUser() || DEV_USER;
  const ORG_ID = user.restaurant_id;
  const LOCATION = user.location;

  const FALLBACK = (menuData?.menu?.items ?? []).map((it) => ({
    item_id: it.id ?? it.item_id,
    item_name: it.name ?? it.item_name,
    available: !!it.available,
    price: Number.isFinite(Number(it.price)) ? Number(it.price) : undefined,
    is_gst: !!it.is_gst,
  }));

  const [, setBase] = useState(FALLBACK);
  const [items, setItems] = useState(FALLBACK);
  const [fromApi, setFromApi] = useState(false);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const INITIAL_BATCH = 30;
  const BATCH = 20;
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(INITIAL_BATCH);

  const [changes, setChanges] = useState(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await fetchMenuItems({ orgId: ORG_ID, location: LOCATION });
      const norm = (list || []).map((it) => ({
        item_id: it.item_id ?? it.id,
        item_name: it.item_name ?? it.name,
        available: !!it.available,
        is_gst: !!it.is_gst,
        price: Number(it.price),
      }));
      setBase(norm);
      setItems(norm);
      setFromApi(true);
      setChanges(new Map());
    } catch (e) {
      setFromApi(false);
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, [ORG_ID, LOCATION]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.item_name || "").toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    setLimit(INITIAL_BATCH);
  }, [query, filtered.length]);

  const visible = useMemo(
    () => filtered.slice(0, Math.min(limit, filtered.length)),
    [filtered, limit]
  );

  const listRef = useRef(null);
  const onScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom) setLimit((p) => Math.min(p + BATCH, filtered.length));
  };

  const effectiveAvail = (row) =>
    changes.has(row.item_id)
      ? changes.get(row.item_id).available
      : row.available;

  const toggleItem = (row) => {
    const original = !!row.available;
    const next = !effectiveAvail(row);
    setChanges((prev) => {
      const m = new Map(prev);
      if (next === original) m.delete(row.item_id);
      else
        m.set(row.item_id, {
          available: next,
          is_gst: row.is_gst, // 👈 preserved from API
        });
      return m;
    });
  };

  const handleSubmit = async () => {
    if (submitting || changes.size === 0) return;

    if (!fromApi) {
      const hasMissingPrice = Array.from(changes.keys()).some(
        (id) => items.find((x) => x.item_id === id)?.price === undefined
      );
      if (hasMissingPrice) {
        alert(
          "Menu loaded from local JSON; missing price. Submit is disabled."
        );
        return;
      }
    }

    const priceById = new Map(items.map((x) => [x.item_id, Number(x.price)]));
    const patches = Array.from(changes.entries()).map(
      ([item_id, change]) => ({
        item_id,
        available: change.available,
        is_gst: change.is_gst,
        price: priceById.get(item_id),
      })
    );

    setSubmitting(true);
    setErr(null);

    try {
      await bulkUpdateMenu(patches);

      setItems((prev) =>
        prev.map((it) =>
          changes.has(it.item_id)
            ? { ...it, available: changes.get(it.item_id) }
            : it
        )
      );

      await load();
    } catch (e) {
      setErr(e);
    } finally {
      setSubmitting(false);
      setChanges(new Map());
    }
  };

  const pendingCount = changes.size;

  return (
    <aside className="dash-card sidebar-menu">
      <div className="menu-header">
        <div className="card-title-row" style={{ marginBottom: 12 }}>
          <div className="card-title">Menu</div>
          <div className="card-subtitle">
            {loading ? "Loading…" : `${filtered.length} items`}
            {pendingCount ? ` • ${pendingCount} pending` : ""}
          </div>
          {/* <div className="card-subtitle">
            {loading ? (
              "Loading…"
            ) : (
              <>
                Total: {stats.total} • Available: {stats.available} •
                Unavailable: {stats.unavailable}
              </>
            )}
            {pendingCount ? ` • ${pendingCount} pending` : ""}
          </div> */}
        </div>

        <input
          type="search"
          placeholder="Search menu…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search menu"
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid var(--border)",
          }}
        />
        {!fromApi && (
          <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
            Showing local data (API not available). Submit may be disabled.
          </div>
        )}
        {err && (
          <div
            className="error"
            style={{ marginTop: 8, fontSize: 12, color: "var(--danger)" }}
          >
            {String(err.message || err)}
          </div>
        )}
      </div>

      <div className="menu-list" ref={listRef} onScroll={onScroll}>
        {visible.map((it) => {
          const checked = effectiveAvail(it);
          return (
            <div key={it.item_id} className="menu-row">
              <div
                className="menu-name"
                title={it.item_name}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {it.item_name}
              </div>

              <label
                className="menu-toggle"
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleItem(it)}
                  aria-label={`Toggle availability for ${it.item_name}`}
                  disabled={loading || submitting}
                />
                <span className="visually-hidden">Available</span>
              </label>
            </div>
          );
        })}

        {visible.length === 0 && !loading && (
          <div
            className="menu-row"
            style={{ justifyContent: "center", color: "var(--muted)" }}
          >
            No items found
          </div>
        )}
      </div>

      <div className="menu-submit">
        <button
          onClick={handleSubmit}
          disabled={submitting || pendingCount === 0}
        >
          {submitting
            ? "Submitting…"
            : pendingCount
            ? `Submit (${pendingCount})`
            : "Submit"}
        </button>
      </div>
    </aside>
  );
}
